import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  HelpCircle, ChevronRight, Lightbulb, X, Sparkles,
  ArrowRight, CheckCircle2, Info, Zap, BookOpen, 
  MapPin, Target, Compass
} from 'lucide-react';

// ==========================================
// WORKFLOW STEP DEFINITIONS
// ==========================================
const WORKFLOW_STEPS = {
  'floor-plan': {
    order: 1,
    name: 'Floor Plan',
    icon: '📐',
    isEssential: true,
    essentialLabel: 'Essential',
    purpose: 'Define your spaces',
    purposeDetail: 'This is where you upload floor plans and draw polygons to identify each room and space. These room definitions become the foundation for all downstream calculations.',
    workflowContext: 'Start here to establish the physical layout before generating quantities.',
    description: 'Upload and annotate floor plans with room polygons',
    nextStep: 'takeoff',
    nextLabel: 'Generate Takeoff',
    tips: [
      'Draw polygons to define rooms and spaces',
      'Label each polygon with room name and type',
      'Use the measurement tool for accurate dimensions'
    ]
  },
  'takeoff': {
    order: 2,
    name: 'Takeoff',
    icon: '📏',
    isEssential: true,
    essentialLabel: 'Essential',
    purpose: 'Extract measurements',
    purposeDetail: 'Review and refine the auto-calculated quantities from your floor plan polygons. This includes square footage, wall areas, perimeters, and counts for doors/windows.',
    workflowContext: 'These measurements feed directly into your Scope of Work for accurate quantity estimates.',
    description: 'Extract measurements and quantities from floor plans',
    nextStep: 'scope',
    nextLabel: 'Generate Scope',
    tips: [
      'Review auto-calculated areas and perimeters',
      'Add door and window counts for each room',
      'Click "Generate Scope" to create scope items'
    ]
  },
  'clips': {
    order: 3,
    name: 'Clips Library',
    icon: '📎',
    isEssential: false,
    essentialLabel: 'Optional - Enhances Traceability',
    purpose: 'Save material selections',
    purposeDetail: 'Your personal library of material selections, finishes, and specifications. Each clip can include images, vendor info, and SpecNum codes for tracking.',
    workflowContext: 'Link clips to scope items to maintain traceability from design decisions to final estimates.',
    description: 'Save material selections and specifications',
    nextStep: 'scope',
    nextLabel: 'Link to Scope',
    tips: [
      'Add SpecNum codes for material tracking',
      'Include images and vendor information',
      'Link clips to scope items for traceability'
    ]
  },
  'scope': {
    order: 4,
    name: 'Scope Builder',
    icon: '📋',
    isEssential: true,
    essentialLabel: 'Essential',
    purpose: 'Define the work to be done',
    purposeDetail: 'Build your comprehensive Scope of Work by room. Each room contains line items describing what will be built, installed, or modified. Link materials from your Clips library for complete traceability.',
    workflowContext: 'The scope becomes the source of truth for bid packages and detailed estimates.',
    description: 'Define the scope of work with detailed items',
    nextStep: 'bid-packages',
    nextLabel: 'Create Bid Package',
    tips: [
      'Apply assemblies to quickly populate rooms',
      'Link materials from Clips library',
      'Use "To Estimate" to generate detailed estimate'
    ]
  },
  'bid-packages': {
    order: 5,
    name: 'Bid Packages',
    icon: '📦',
    isEssential: false,
    essentialLabel: 'Optional - For Contractor Bidding',
    purpose: 'Bundle work for contractors',
    purposeDetail: 'Package your scope items by trade (Plumbing, Electrical, HVAC, etc.) along with relevant drawings. Send these packages to contractors to receive competitive bids.',
    workflowContext: 'Contractor quotes from bid packages can be added directly to your detailed estimate.',
    description: 'Bundle scope and drawings for contractor bidding',
    nextStep: 'estimate',
    nextLabel: 'View Estimate',
    tips: [
      'Create packages by trade (Plumbing, Electrical, etc.)',
      'Attach relevant drawings',
      'Send to contractors for quotes'
    ]
  },
  'estimate': {
    order: 6,
    name: 'Detailed Estimate',
    icon: '💰',
    isEssential: true,
    essentialLabel: 'Essential - Final Output',
    purpose: 'Build the client proposal',
    purposeDetail: 'The final destination for all your project data. Combine material costs, labor, contractor quotes, and markups into a comprehensive estimate ready for client presentation.',
    workflowContext: 'This is where everything comes together for the client proposal and contract.',
    description: 'Build comprehensive cost estimates for clients',
    nextStep: null,
    nextLabel: null,
    tips: [
      'Add contractor quotes as lump sum items',
      'Use assemblies for common room configurations',
      'Generate client proposals and contracts'
    ]
  }
};

// ==========================================
// PAGE PURPOSE COMPONENT - Shows what this page is for
// ==========================================
export const PagePurpose = ({ 
  currentStep, 
  darkMode = true,
  showWorkflowPosition = true,
  compact = false,
  className = '' 
}) => {
  const stepConfig = WORKFLOW_STEPS[currentStep];
  if (!stepConfig) return null;
  
  const totalSteps = Object.keys(WORKFLOW_STEPS).length;
  
  if (compact) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${darkMode ? 'bg-[#1A1A1A] border border-[#2A2A2A]' : 'bg-white border border-[#E5E2DD]'}`}>
          <span className="text-lg">{stepConfig.icon}</span>
          <div>
            <p className={`text-sm font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {stepConfig.purpose}
            </p>
            {showWorkflowPosition && (
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                Step {stepConfig.order} of {totalSteps} • {stepConfig.isEssential ? '✓ Essential' : 'Optional'}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`rounded-xl border ${darkMode ? 'bg-gradient-to-r from-[#1A1A1A] to-[#0D0D0D] border-[#2A2A2A]' : 'bg-gradient-to-r from-white to-[#FAF9F7] border-[#E5E2DD]'} ${className}`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon & Step Number */}
          <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${darkMode ? 'bg-[#0D0D0D] border border-[#2A2A2A]' : 'bg-[#FAF9F7] border border-[#E5E2DD]'}`}>
            {stepConfig.icon}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
                {stepConfig.purpose}
              </h2>
              {showWorkflowPosition && (
                <Badge variant="outline" className={`text-xs ${darkMode ? 'border-[#BC5D41]/50 text-[#BC5D41]' : 'border-[#BC5D41]/50 text-[#BC5D41]'}`}>
                  <Compass className="w-3 h-3 mr-1" />
                  Step {stepConfig.order} of {totalSteps}
                </Badge>
              )}
              {/* Essential/Optional Badge */}
              <Badge className={`text-xs ${
                stepConfig.isEssential 
                  ? 'bg-green-500/20 text-green-500 border border-green-500/30' 
                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              }`}>
                {stepConfig.isEssential ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> {stepConfig.essentialLabel}</>
                ) : (
                  <><Info className="w-3 h-3 mr-1" /> {stepConfig.essentialLabel}</>
                )}
              </Badge>
            </div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-[#6B6965]'}`}>
              {stepConfig.purposeDetail}
            </p>
            <p className={`text-xs mt-2 flex items-center gap-1.5 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
              <Target className="w-3.5 h-3.5" />
              {stepConfig.workflowContext}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// CONTEXTUAL HINT COMPONENT
// ==========================================
export const ContextualHint = ({ 
  children, 
  hint, 
  position = 'top',
  showIcon = true,
  className = ''
}) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1 cursor-help ${className}`}>
            {children}
            {showIcon && <Info className="w-3.5 h-3.5 text-blue-400 opacity-60" />}
          </span>
        </TooltipTrigger>
        <TooltipContent side={position} className="max-w-xs bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]">
          <p className="text-sm">{hint}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ==========================================
// NEXT STEP PROMPT COMPONENT
// ==========================================
export const NextStepPrompt = ({ 
  currentStep, 
  onNavigate, 
  darkMode = true,
  compact = false,
  className = '' 
}) => {
  const [dismissed, setDismissed] = useState(false);
  const stepConfig = WORKFLOW_STEPS[currentStep];
  
  if (!stepConfig || !stepConfig.nextStep || dismissed) return null;
  
  const nextConfig = WORKFLOW_STEPS[stepConfig.nextStep];
  
  if (compact) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate?.(stepConfig.nextStep)}
        className={`gap-2 ${darkMode ? 'border-[#BC5D41]/50 text-[#BC5D41] hover:bg-[#BC5D41]/10' : 'border-[#BC5D41]/50 text-[#BC5D41] hover:bg-[#BC5D41]/10'} ${className}`}
      >
        <ArrowRight className="w-4 h-4" />
        {stepConfig.nextLabel}
      </Button>
    );
  }
  
  return (
    <div className={`rounded-lg border p-4 ${darkMode ? 'bg-[#1A1A1A]/50 border-[#BC5D41]/30' : 'bg-[#FDF8F6] border-[#BC5D41]/30'} ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#BC5D41]/20' : 'bg-[#BC5D41]/10'}`}>
            <Sparkles className="w-5 h-5 text-[#BC5D41]" />
          </div>
          <div>
            <p className={`text-sm font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              Ready for the next step?
            </p>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-[#6B6965]'}`}>
              {stepConfig.nextLabel} → {nextConfig?.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => onNavigate?.(stepConfig.nextStep)}
            className="bg-[#70170F] hover:bg-[#8B1D12] text-white"
          >
            {stepConfig.nextLabel}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setDismissed(true)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// WORKFLOW PROGRESS INDICATOR
// ==========================================
export const WorkflowProgress = ({ 
  currentStep, 
  completedSteps = [], 
  onStepClick,
  darkMode = true,
  compact = false,
  className = '' 
}) => {
  const steps = Object.entries(WORKFLOW_STEPS)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, config]) => ({ key, ...config }));
  
  const currentIndex = steps.findIndex(s => s.key === currentStep);
  
  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.key);
          const isCurrent = step.key === currentStep;
          const isPast = index < currentIndex;
          
          return (
            <TooltipProvider key={step.key} delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onStepClick?.(step.key)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      isCompleted ? 'bg-green-500' :
                      isCurrent ? 'bg-[#BC5D41] ring-2 ring-[#BC5D41]/30' :
                      isPast ? 'bg-gray-500' : 
                      darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-300'
                    }`}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <p className="text-xs text-[#E5E2DD]">{step.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-2 overflow-x-auto pb-2 ${className}`}>
      {steps.map((step, index) => {
        const isCompleted = completedSteps.includes(step.key);
        const isCurrent = step.key === currentStep;
        const isLast = index === steps.length - 1;
        
        return (
          <React.Fragment key={step.key}>
            <button
              onClick={() => onStepClick?.(step.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                isCurrent 
                  ? 'bg-[#70170F] text-white' 
                  : isCompleted 
                    ? (darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700')
                    : (darkMode ? 'bg-[#1A1A1A] text-gray-400 hover:bg-[#2A2A2A]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
              }`}
            >
              {isCompleted ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                  isCurrent ? 'bg-white/20' : darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'
                }`}>
                  {step.order}
                </span>
              )}
              {step.name}
            </button>
            {!isLast && (
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ==========================================
// TIPS PANEL COMPONENT
// ==========================================
export const TipsPanel = ({ 
  currentStep, 
  darkMode = true,
  collapsible = true,
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const stepConfig = WORKFLOW_STEPS[currentStep];
  
  if (!stepConfig) return null;
  
  return (
    <div className={`rounded-lg border ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'} ${className}`}>
      <button
        onClick={() => collapsible && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-3 ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500" />
          <span className={`text-sm font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
            Tips for {stepConfig.name}
          </span>
        </div>
        {collapsible && (
          <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''} ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        )}
      </button>
      
      {isOpen && (
        <div className={`px-3 pb-3 space-y-2 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}>
          {stepConfig.tips.map((tip, index) => (
            <div key={index} className="flex items-start gap-2 pt-2">
              <Zap className="w-3.5 h-3.5 text-[#BC5D41] mt-0.5 flex-shrink-0" />
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-[#6B6965]'}`}>{tip}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// HELP BUTTON WITH QUICK GUIDE
// ==========================================
export const QuickHelpButton = ({ 
  currentStep,
  darkMode = true,
  className = '' 
}) => {
  const [showGuide, setShowGuide] = useState(false);
  const stepConfig = WORKFLOW_STEPS[currentStep];
  
  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowGuide(!showGuide)}
              className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} ${className}`}
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-[#1A1A1A] border-[#2A2A2A]">
            <p className="text-xs text-[#E5E2DD]">Quick Help</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {showGuide && stepConfig && (
        <div className={`absolute right-0 top-full mt-2 w-80 rounded-lg border shadow-xl z-50 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#BC5D41]" />
                <h3 className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
                  {stepConfig.name}
                </h3>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowGuide(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-[#6B6965]'}`}>
              {stepConfig.description}
            </p>
            <div className="space-y-2">
              {stepConfig.tips.map((tip, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-[#1A1918]'}`}>{tip}</p>
                </div>
              ))}
            </div>
            {stepConfig.nextStep && (
              <div className={`mt-4 pt-3 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                  Next: {stepConfig.nextLabel} → {WORKFLOW_STEPS[stepConfig.nextStep]?.name}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ==========================================
// FEATURE HIGHLIGHT BADGE
// ==========================================
export const FeatureHighlight = ({ 
  label = 'New',
  variant = 'new', // 'new', 'tip', 'important'
  className = '' 
}) => {
  const variants = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    tip: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    important: 'bg-[#BC5D41]/20 text-[#BC5D41] border-[#BC5D41]/30'
  };
  
  return (
    <Badge className={`text-[10px] px-1.5 py-0 border ${variants[variant]} ${className}`}>
      {variant === 'tip' && <Lightbulb className="w-2.5 h-2.5 mr-1" />}
      {variant === 'new' && <Sparkles className="w-2.5 h-2.5 mr-1" />}
      {label}
    </Badge>
  );
};

export default {
  PagePurpose,
  ContextualHint,
  NextStepPrompt,
  WorkflowProgress,
  TipsPanel,
  QuickHelpButton,
  FeatureHighlight,
  WORKFLOW_STEPS
};
